import { useState, useEffect } from 'react';
import { Loader2, Trophy, Flame } from 'lucide-react';
import {
  UserPlus,
  Users,
  Zap,
  Eye,
  TrendingUp,
  Globe,
  CheckCircle,
  CheckSquare,
  Rocket,
  Award,
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { getBadgeProgress, getUserStreak, getUserBadges } from '../../services/gamificationService';
import type { CategoryProgress, BadgeTier, UserStreak, UserBadge } from '../../types/gamification';
import { TIER_COLORS, CATEGORY_LABELS, TIER_LABELS } from '../../types/gamification';
import { logger } from '../../lib/logger';

const ICONS: Record<string, React.ComponentType<{ className?: string }>> = {
  UserPlus,
  Users,
  Magnet: Zap,
  Zap,
  Eye,
  TrendingUp,
  Globe,
  Trophy,
  CheckCircle,
  CheckSquare,
  Rocket,
  Award,
  Flame,
};

export function BadgeProgressCompact() {
  const { user } = useAuth();
  const [isLoading, setIsLoading] = useState(true);
  const [categories, setCategories] = useState<CategoryProgress[]>([]);
  const [streak, setStreak] = useState<UserStreak | null>(null);
  const [earnedBadges, setEarnedBadges] = useState<UserBadge[]>([]);

  const loadData = async () => {
    if (!user) return;
    setIsLoading(true);
    try {
      const [progressData, streakData, badgesData] = await Promise.all([
        getBadgeProgress(user.id),
        getUserStreak(user.id),
        getUserBadges(user.id),
      ]);
      setCategories(progressData);
      setStreak(streakData);
      setEarnedBadges(badgesData);
    } catch (error) {
      logger.error('Error loading badge progress:', error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, [user]);

  // Refresh data when window gains focus (user returns to tab)
  useEffect(() => {
    const handleFocus = () => {
      if (user) loadData();
    };

    window.addEventListener('focus', handleFocus);
    return () => window.removeEventListener('focus', handleFocus);
  }, [user]);

  if (isLoading) {
    return (
      <div className="bg-white dark:bg-slate-800 rounded-2xl border border-gray-100 dark:border-slate-700 p-6 shadow-sm">
        <div className="flex items-center justify-center py-4">
          <Loader2 className="w-5 h-5 text-primary animate-spin" />
        </div>
      </div>
    );
  }

  // Get current and next badge for each category
  const getCategoryStatus = (category: CategoryProgress) => {
    const earnedBadges = category.badges.filter(b => b.earned);
    const currentBadge = earnedBadges.length > 0 ? earnedBadges[earnedBadges.length - 1] : null;
    const nextBadge = category.badges.find(b => !b.earned) || null;
    return { currentBadge, nextBadge };
  };

  // Determine flame color based on streak
  const getFlameColor = () => {
    const currentStreak = streak?.current_streak || 0;
    if (currentStreak >= 30) return 'text-violet-500';
    if (currentStreak >= 14) return 'text-yellow-500';
    if (currentStreak >= 7) return 'text-orange-500';
    if (currentStreak >= 3) return 'text-red-500';
    return 'text-gray-400 dark:text-slate-500';
  };

  return (
    <div className="bg-white dark:bg-slate-800 rounded-2xl border border-gray-100 dark:border-slate-700 p-6 shadow-sm">
      {/* Earned Badges Section */}
      {earnedBadges.length > 0 && (
        <div className="mb-6 pb-6 border-b border-gray-100 dark:border-slate-700">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2 mb-4">
            <Award className="w-5 h-5 text-primary" />
            Deine Badges
          </h3>
          <div className="flex flex-wrap gap-3">
            {earnedBadges.map((ub) => {
              if (!ub.badge) return null;
              const IconComponent = ICONS[ub.badge.icon] || Trophy;
              const tierColors = TIER_COLORS[ub.badge.tier as BadgeTier];
              return (
                <div
                  key={ub.id}
                  className={`flex items-center gap-2 px-3 py-2 rounded-xl border ${tierColors.bg} ${tierColors.border}`}
                  title={ub.badge.description || ub.badge.name}
                >
                  <IconComponent className={`w-5 h-5 ${tierColors.text}`} />
                  <div>
                    <p className="text-sm font-medium text-gray-900 dark:text-white">{ub.badge.name}</p>
                    <p className={`text-xs ${tierColors.text}`}>{TIER_LABELS[ub.badge.tier as BadgeTier]}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      <div className="flex items-center justify-between mb-5">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
          <Trophy className="w-5 h-5 text-primary" />
          Dein Fortschritt
        </h3>

        {/* Streak Display */}
        {streak && streak.current_streak > 0 && (
          <div className="flex items-center gap-1.5 px-3 py-1.5 bg-orange-50 dark:bg-orange-900/20 rounded-full">
            <Flame className={`w-4 h-4 ${getFlameColor()}`} />
            <span className="text-sm font-semibold text-orange-700 dark:text-orange-400">
              {streak.current_streak} {streak.current_streak === 1 ? 'Tag' : 'Tage'}
            </span>
          </div>
        )}
      </div>

      <div className="space-y-4">
        {categories.map((category) => {
          const { currentBadge, nextBadge } = getCategoryStatus(category);
          const displayBadge = nextBadge || currentBadge;

          if (!displayBadge) return null;

          const IconComponent = ICONS[displayBadge.badge.icon] || Trophy;
          const progress = nextBadge
            ? Math.min((category.currentValue / nextBadge.badge.threshold) * 100, 100)
            : 100;
          const tierColors = currentBadge
            ? TIER_COLORS[currentBadge.badge.tier as BadgeTier]
            : { bg: 'bg-gray-100 dark:bg-slate-700', text: 'text-gray-500 dark:text-slate-400', border: '' };

          return (
            <div key={category.category} className="flex items-center gap-4">
              {/* Badge Icon */}
              <div className={`w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 ${currentBadge ? tierColors.bg : 'bg-gray-100 dark:bg-slate-700'}`}>
                <IconComponent className={`w-6 h-6 ${currentBadge ? tierColors.text : 'text-gray-400 dark:text-slate-500'}`} />
              </div>

              {/* Progress Info */}
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-sm font-medium text-gray-900 dark:text-white truncate">
                    {CATEGORY_LABELS[category.category]}
                  </span>
                  <span className="text-xs text-gray-500 dark:text-slate-400 flex-shrink-0 ml-2">
                    {category.currentValue.toLocaleString('de-DE')} / {(nextBadge?.badge.threshold || currentBadge?.badge.threshold || 0).toLocaleString('de-DE')}
                  </span>
                </div>

                {/* Progress Bar */}
                <div className="h-2 bg-gray-100 dark:bg-slate-700 rounded-full overflow-hidden">
                  <div
                    className={`h-full rounded-full transition-all duration-300 ${
                      nextBadge ? 'bg-primary' : 'bg-emerald-500'
                    }`}
                    style={{ width: `${progress}%` }}
                  />
                </div>

                {/* Next Badge Name */}
                {nextBadge && (
                  <p className="text-xs text-gray-500 dark:text-slate-400 mt-1">
                    Nächstes: {nextBadge.badge.name}
                  </p>
                )}
                {!nextBadge && currentBadge && (
                  <p className="text-xs text-emerald-600 dark:text-emerald-400 mt-1">
                    ✓ {currentBadge.badge.name} erreicht
                  </p>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
